import type { Phase } from "@/lib/types";

// Gates & Dates — 9 canonical rows (5 phases + 4 gates). Every deliverable
// in the Tracker rolls up under one of the 5 phase names via its `phase`
// column (derived from due-date). Phase-level metadata (audience, purpose,
// notes, risks) lives here; gate criteria live in the Criteria tab.
export const PHASES: Phase[] = [
  {
    id: "alpha",
    name: "Alpha",
    status: "complete",
    date: "Wk of Mar 23",
    owner: "Ryan",
    progress: 100,
    isGate: false,
    audience: "Young Team (internal)",
    purpose: "Test UX, AI accuracy, surface bugs in safe account.",
    gateCriteria: "Core product works E2E. Value visible internally. Trust enough to show externals.",
    notes: "Completed.",
  },
  {
    id: "gate1",
    name: "GATE: Alpha → Alpha+",
    status: "complete",
    date: "Apr 11",
    owner: "ELT",
    progress: 100,
    isGate: true,
    audience: "ELT",
    purpose: "Decide whether Alpha results justify exposing to trusted external accounts.",
    gateCriteria: "Core product works. Value visible internally. No P0 blockers. ELT confident to show externals.",
    risksBlockers: "If not met: delay Alpha+ until resolved.",
    notes: "ELT made proceed call. Alpha+ now active.",
  },
  {
    id: "alpha_plus",
    name: "Alpha+",
    status: "complete",
    date: "Apr 15 – May 11",
    owner: "Stephen",
    progress: 45,
    isGate: false,
    audience: "5 trusted customers/investors (Gellman, Lori, Duncan, Realtor.com)",
    purpose: "Validate E2E usage in live customer environments after Alpha feedback addressed.",
    gateCriteria: "Friendly customers getting measurable value. Every major friction point identified & addressed. Token/inference efficiency on track.",
    risksBlockers: "EB launch Apr 15 will tax resources. Twilio approval process starts now for beta accounts (Tom owns).",
    notes: [
      "Co-founders + product team support. Hand-holding expected, every intervention logged as a gap.",
      "",
      "Parallel workstreams during Alpha+:",
      "• Twilio Setup for Beta (Apr 14–27, Ryan + Tom) — Ryan invites 20 beta accounts with Twilio setup requirements; Tom shepherds through registration and approval (2+ weeks). All approvals confirmed before May 4 webinar.",
      "• Teaser Promos + Felix Socials (Wk of Apr 27) — Ryan/Stephen hype, Nikhil Felix profiles, Seth teaser LP. Social calendar approved, LP live with email capture.",
      "• Pricing & Packaging kickoff (Apr 21 onward, Stephen) — customer pricing discovery calls during Alpha+. Full framework finalizes May 15.",
      "• Beta Webinar (Wk of May 4, Ryan; Lisa on logistics) — webinar executed, cohort confirmed and Twilio-approved, messaging doc + competitive guide shared with Sales.",
    ].join("\n"),
  },
  {
    id: "gate2",
    name: "GATE: Alpha+ → Closed Beta",
    status: "complete",
    date: "May 2",
    owner: "ELT",
    progress: 0,
    isGate: true,
    audience: "ELT",
    purpose: "Decide whether Alpha+ results justify expanding to 20 curated accounts.",
    gateCriteria: "Friendly customers getting measurable value. Every major friction point identified & addressed. No P0 blockers for beta. Token/inference efficiency on track to support beta cohort. Twilio approvals confirmed for 20 accounts.",
    risksBlockers: "If not met: delay beta. ELT must name what needs to be true before proceeding.",
    notes: "ELT makes proceed/delay call. This is the highest-stakes gate — first time non-friendlies use Felix.",
  },
  {
    id: "closed_beta_1",
    name: "Closed Beta 1",
    status: "active",
    date: "May 11 – Jun 22",
    owner: "Tom / Ryan / Dustin",
    progress: 0,
    isGate: false,
    audience: "12 fully-assisted onboarding customers",
    purpose: "Prove value outside our network. Prove the onboarding can start scaling. RIF starts showing up.",
    gateCriteria: "12 customers achieve TTV1 + TTV2 (1 handoff in 14 days OR 1/1k contacts; 1 listing appt in 14 days). Simulate additional contacts: enroll ~5k contacts in marketing & sales agent, then add 5k more after 14 days.",
    risksBlockers: "FLAG: Pricing framework finalization (Stephen, May 15) blocks pricing page, ROI calculator, Sales sequences. Round 2 demand-build (Jun 1 webinar + ads) drives demand into Closed Beta 2 — if Round 1 gate slips, demand fires into an empty queue.",
    notes: [
      "Support model: Co-founders + product team + Courtney + Dustin + Kerry.",
      "Gate owner: Tom + Ryan + Dustin. Gate review: Co-founders.",
      "",
      "Parallel workstreams during Closed Beta 1:",
      "• Pricing & Packaging finalize (May 15, Stephen) — cost estimate from Product/Eng received, customer calls complete, XFN review done, ELT approves framework.",
      "• Marketing Push (Wk of May 17, Lisa growth; Seth design; Jason product marketing) — email campaigns, full Felix webpage, ads, landing page, partner comms, 3–4 Ryan podcasts booked.",
      "• Press / Analyst Decision (Late May, Stephen) — yes/no on GA press release. If yes: target list + embargo timeline set.",
      "• Website Full Refresh (May 17 → Jun 22, Seth) — teaser LP Apr 27, full Felix page May 17, homepage Jun 1, product pages Jun 8, pricing page at GA (blocked until pricing locks).",
    ].join("\n"),
  },
  {
    id: "gate3",
    name: "GATE: Closed Beta 1 → Closed Beta 2",
    status: "upcoming",
    date: "Jun 1",
    owner: "ELT",
    progress: 0,
    isGate: true,
    audience: "ELT",
    purpose: "Decide whether Round 1 results justify opening to a wider, less-assisted ICP cohort.",
    gateCriteria: "Real customers getting measurable outcomes. TTV1 hit rate ≥70% in Round 1. Friction measurably lower than Alpha+. A/B test results reviewed — onboarding model for Round 2 decided. CS capacity plan confirmed (Courtney/Dustin). Self-serve phone/SMS registration live. Scalability proven at Round 1 scale with headroom for Round 2.",
    risksBlockers: "If not met: delay Round 2. ELT must name what needs to be true. A/B test results directly determine CS staffing and onboarding model.",
    notes: "ELT makes proceed/delay call. Round 2 is the dress rehearsal for GA — the bar is real.",
  },
  {
    id: "closed_beta_2",
    name: "Closed Beta 2",
    status: "upcoming",
    date: "Jun 1 – Jun 22",
    owner: "Stephen / Ryan / Tom / Dustin / Courtney",
    progress: 0,
    isGate: false,
    audience: "ICP-fit customers — no cohort cap, ramp controlled",
    purpose: "10 customers set up themselves with CS/Solutions assisting throughout onboarding. 10 customers onboard themselves and deploy Felix, achieving 1 handoff/1000 contacts/10 days.",
    gateCriteria: "Value consistent across ICP. GTM + CS + Solutions own the relationship. Product team for escalation only. Self-onboarding works for the bulk of customers; assisted-onboarding works for the rest. Scalability proven for GA.",
    risksBlockers: "FLAG: CS capacity for the wider ICP cohort depends on A/B test results from Round 1. If high-touch wins, Courtney/Dustin must confirm headcount. If self-serve wins, product must confirm Felix onboarding handles it.",
    notes: [
      "Support model: GTM + CS + Solutions own the relationship. Product for escalation only.",
      "Gate owner: Stephen + Ryan + Tom + Dustin + Courtney. Gate review: Leadership + GTM leads — 90-min sync.",
      "",
      "Demand build (overlaps end of Closed Beta 1):",
      "• Round 2 demand build (Wk of Jun 1, Lisa) — webinar with Round 1 customers as speakers, CTA: talk to CS rep. Ads approved and live. Case study #1 ready.",
    ].join("\n"),
  },
  {
    id: "gate4",
    name: "GATE: Closed Beta 2 → GA",
    status: "upcoming",
    date: "Jun 19",
    owner: "ELT",
    progress: 0,
    isGate: true,
    audience: "ELT",
    purpose: "Decide whether Closed Beta 2 results justify full market launch.",
    gateCriteria: "FIR instrumented, validated, and visible to customers without effort. Value consistent across ICP cohort. Felix handles ~90% of onboarding. CS + Solutions handle remaining ~10%. Product team out of support loop. Both skills producing reliable outcomes. Infra scales to demand. No unresolved P0s.",
    risksBlockers: "If not met: delay GA. ELT must name what needs to be true and set a revised target.",
    notes: "ELT makes proceed/delay call. This is the final gate before full market exposure.",
  },
  {
    id: "ga",
    name: "GA",
    status: "upcoming",
    date: "Wk of Jun 22",
    owner: "Stephen",
    progress: 0,
    isGate: false,
    audience: "Full market — prospects + customers",
    purpose: "External launch. Scale demand with confidence, not anxiety.",
    gateCriteria: "FIR instrumented & visible to customers. Felix ~90% onboarding. Both skills reliable. Infra scales. Website fully refreshed including pricing page.",
    notes: [
      "Post-launch workstreams:",
      "• Launch Complete (Wk of Jul 6, Jason) — all GTM activities complete: website, email, social, video, in-app messages, case studies live and distributed. 30-day check-in follows.",
      "• Launch Retro (Jul 13, Stephen) — structured retrospective: retro scheduled, all workstream leads attend, action items documented.",
    ].join("\n"),
  },
];
